/**
 * 主题详情页 /themes/$id（旧 views/themes/ThemeDetailView.vue）
 */
import { Badge, Button, Card, Col, Descriptions, Result, Row, Spin, Tag } from 'antd'
import { getRouteApi, useNavigate } from '@tanstack/react-router'
import { PageHeader } from '@/components/PageHeader'
import { MarkdownView } from '@/components/MarkdownView'
import { categoryColor, categoryLabel, formatThemeDate } from './constants'
import { useThemeDetail, useThemeStats } from './hooks'
import { ThemeUsageStats } from './ThemeUsageStats'
import styles from './ThemeDetailPage.module.css'

const routeApi = getRouteApi('/admin/themes/$id')

export function ThemeDetailPage() {
  const { id } = routeApi.useParams()
  const navigate = useNavigate()
  const { data: theme, isLoading } = useThemeDetail(id)
  const { data: stats } = useThemeStats(id)
  const goBack = () => {
    navigate({ to: '/themes' })
  }

  const header = (
    <PageHeader title={`主题详情 - ${theme?.title || ''}`} subtitle="查看主题的详细信息和使用统计" onBack={goBack} />
  )

  if (isLoading) {
    return (
      <div>
        {header}
        <div className={styles.loading}>
          <Spin size="large" />
        </div>
      </div>
    )
  }

  if (!theme) {
    return (
      <div>
        {header}
        <Result
          status="404"
          title="主题不存在"
          subTitle="请检查主题ID是否正确"
          extra={
            <Button type="primary" onClick={goBack}>
              返回列表
            </Button>
          }
        />
      </div>
    )
  }

  return (
    <div>
      {header}
      <Card title="基本信息" className={styles.section}>
        <Descriptions column={2} bordered>
          <Descriptions.Item label="主题代码">
            <Tag color="blue">{theme.code}</Tag>
          </Descriptions.Item>
          <Descriptions.Item label="主题标题">
            <MarkdownView content={theme.title} inline />
          </Descriptions.Item>
          <Descriptions.Item label="分类">
            <Tag color={categoryColor(theme.category)}>{categoryLabel(theme.category)}</Tag>
          </Descriptions.Item>
          <Descriptions.Item label="语言">
            <Tag color="blue">{theme.locale}</Tag>
          </Descriptions.Item>
          <Descriptions.Item label="状态">
            <Tag color={theme.is_active ? 'success' : 'default'}>{theme.is_active ? '启用' : '禁用'}</Tag>
          </Descriptions.Item>
          <Descriptions.Item label="版本">
            <Badge count={theme.version} color="green" />
          </Descriptions.Item>
          <Descriptions.Item label="创建时间" span={2}>
            {formatThemeDate(theme.created_at)}
          </Descriptions.Item>
          <Descriptions.Item label="更新时间" span={2}>
            {formatThemeDate(theme.updated_at)}
          </Descriptions.Item>
        </Descriptions>
      </Card>

      {theme.description && (
        <Card title="主题描述" className={styles.section}>
          <MarkdownView content={theme.description} />
        </Card>
      )}

      <Card title="使用统计" className={styles.section}>
        <ThemeUsageStats stats={stats} />
      </Card>

      <Row gutter={16}>
        <Col span={12}>
          <Card title="初始固定设定">
            <pre className={styles.json}>{JSON.stringify(theme.initial_fixed_setting, null, 2)}</pre>
          </Card>
        </Col>
        <Col span={12}>
          {theme.style_hints && (
            <Card title="风格提示">
              <pre className={styles.json}>{JSON.stringify(theme.style_hints, null, 2)}</pre>
            </Card>
          )}
        </Col>
      </Row>

      {theme.initial_recap && (
        <Card title="初始回顾" className={styles.recap}>
          <p>{theme.initial_recap}</p>
        </Card>
      )}
    </div>
  )
}
